import {memo} from 'react';

import styled, {useTheme} from 'styled-components';

import {useIsMinLargeTablet} from '~/shared/hooks/deviceInfo';
import {useLocation, useRouteQuery} from '~/shared/router';
import {useSideMenuHandlers, useUserReportData} from '~/common/hooks/UserTransactions';
import {getLocalizationService} from '~/shared/services/localisationService';
import {EMPTY_ARRAY} from '~/shared/consts/commonConsts';
import DateSelection from '~/shared/components/UserTransactions/components/DateSelection';
import TransactionTable from '~/shared/components/UserTransactions/components/TransactionTable';
import {body14Bold, body20Bold} from '~/shared/theme/typography';
import {media} from '~/shared/theme/media';
import ShowOrder from '~/common/components/ShowOrder';
import {flipOnLTR} from '~/shared/theme/flipOnLTR';
import {Skeleton, SkeletonLoader} from '~/shared/components/Loaders';

import EmployerEmployeeCard from './EmployerEmployeeCard';
import ActiveOrders from './ActiveOrders';
import TotalsTable from './TotalsTable';
import MoneyCardDetails from './MoneyCardDetails';
import DebitSentRequestsTable from './DebitSentRequestsTable';
import GroupByDayReport from './GroupByDayReport';
import {NoOrdersThisMonthEmptyState} from './EmptyStates';

const Root = styled.div`
  display: flex;
  width: 100%;
  background: ${({theme}) => theme.colors.surface};
  flex-direction: column;
  align-items: center;
  padding-top: 24px;
  ${media.minLargeMobile`
    padding: 32px;
  `}
  ${media.minLargeTablet`
    padding: 40px 20px;
  `}
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: column-reverse;
  width: 100%;
  margin-bottom: 8px;
  ${media.minLargeMobile`
    margin-bottom: 16px;
    flex-direction: row;
  `}
`;

const Title = styled.h1`
  ${body20Bold}
  line-height: 28px;
  margin-top: 38px;
  color: ${({theme}) => theme.colors.contentDefault};
  ${media.maxLargeMobile`
    ${flipOnLTR`
      margin-right: 16px;
    `}
  `}
  ${media.minLargeMobile`
    margin-top: 0;
  `}
`;

const TitleSkeleton = styled(Skeleton)`
  width: 200px;
  height: 24px;
`;

const DateSelectionWrapper = styled.div`
  display: flex;
  flex-direction: ${({theme}) => (theme.isLTR ? 'row' : 'row-reverse')};
  box-shadow: ${({theme}) => theme.shadows.shadow1};
  position: absolute;
  width: 100vw;
  height: 38px;
  top: -24px;
  ${media.minLargeMobile`
    position: static;
    box-shadow: none;
    width: auto;
    height: auto;
  `}
  ${body14Bold}
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
`;

const MyOrdersWrapper = styled.div`
  ${media.maxLargeMobile`
    position: absolute;
    right: 0;
    left: 0;
    top: 0;
    width: 100vw;
  `}
`;

const StyledTotalsTable = styled(TotalsTable)`
  margin-bottom: 24px;
`;

const StyledDebitSentRequestsTable = styled(DebitSentRequestsTable)`
  margin-bottom: 24px;
`;

const MyOrders = () => {
  const {t, currentLanguageKey} = getLocalizationService();
  const location = useLocation();
  const {query} = location;
  const theme = useTheme();

  const isMinLargeTablet = useIsMinLargeTablet();

  const dateBias = query?.dateBias;

  const {
    data,
    setData,
    currentDateBiasValue,
    numberDateBias,
    isKlaReport,
    allowedToStepForward,
    allowedToStepBackward,
    stepBackward,
    stepForward,
    setOrderListData,
    userActiveOrders,
    isIntelReport,
    isGroupByDayReport,
    onSentDebitTransferCancelClick,
    filter,
    setFilter,
    isTenbisCreditByMoneycardID,
    onMobileTransactionClick,
    isCompanyUser,
    showNoOrdersThisMonth,
    userLoaded,
  } = useUserReportData({dateBias, baseUrl: '/personal-area', additionalQuery: 'section=my-orders'});
  const menuEvents = useSideMenuHandlers({data, setData, dateBias});
  const {showOrder} = useRouteQuery();

  if (showOrder && !isMinLargeTablet) {
    return (
      <MyOrdersWrapper>
        <ShowOrder backButtonText={t('personal_area_page_my_orders_tab')}/>
      </MyOrdersWrapper>
    );
  }

  return (
    <Root>
      <ContentWrapper>
        <HeaderSection>
          <SkeletonLoader shouldShowLoader={!userLoaded} LoaderComponent={TitleSkeleton}>
            <Title>{t(isCompanyUser ? 'charges_report_2' : 'personal_area_page_my_orders_tab')}</Title>
          </SkeletonLoader>
          <DateSelectionWrapper>
            <DateSelection
              startDateStr={currentDateBiasValue?.companyReportRange?.startDateStr}
              endDateStr={currentDateBiasValue?.companyReportRange?.endDateStr}
              allowedToStepForward={allowedToStepForward}
              allowedToStepBackward={allowedToStepBackward}
              stepForward={stepForward}
              stepBackward={stepBackward}
              showMonthOnly
            />
          </DateSelectionWrapper>
        </HeaderSection>
      </ContentWrapper>
      {showNoOrdersThisMonth ? (
        <NoOrdersThisMonthEmptyState />
      ) : (
        <ContentWrapper>
          {(userLoaded && isCompanyUser) && (
            <>
              <MoneyCardDetails
                dateBias={numberDateBias}
                moneyCardsList={currentDateBiasValue?.moneycards}
                reportSetting={currentDateBiasValue?.reportSetting}
                setOrderListData={setOrderListData}
                onAutoCreditSubscribeSuccess={() => setOrderListData()}
              />
              <EmployerEmployeeCard
                marginTop={Boolean(currentDateBiasValue?.reportSetting?.hideMoneycardLimitsAndBalance)}
                dateBias={numberDateBias}
                employeePartSum={currentDateBiasValue?.totals?.employeePartSum}
                employerPartSum={currentDateBiasValue?.totals?.employerPartSum}
              />
              <ActiveOrders
                activeOrders={userActiveOrders}
                orderList={currentDateBiasValue?.orderList}
                tableHeaderCustomColor={theme.colors.contentDisabled}
              />
              <StyledTotalsTable
                payments={currentDateBiasValue?.totals?.payments}
                total={currentDateBiasValue?.totals?.total}
                subsidizedMeals={currentDateBiasValue?.totals?.employerPartSum}
                nettMeals={currentDateBiasValue?.totals?.employeePartSum}
                companyAnnouncement={currentDateBiasValue?.companyAnnouncement}
                isIntelReport={isIntelReport}
                isKlaReport={isKlaReport}
                moneyCardsList={currentDateBiasValue?.moneycards}
                isTenbisCreditByMoneycardID={isTenbisCreditByMoneycardID}
                dateBias={numberDateBias}
                tableHeaderCustomColor={theme.colors.contentDisabled}
                customTableFooterColor={theme.colors.gray920}
              />
              <StyledDebitSentRequestsTable
                debitTransfersSentRequests={currentDateBiasValue?.debitTransfers?.debitTransfersFromUser || EMPTY_ARRAY}
                onSentDebitTransferCancelClick={onSentDebitTransferCancelClick}
                tableHeaderCustomColor={theme.colors.contentDisabled}
              />
            </>
          )}
          {isGroupByDayReport ? (
            <GroupByDayReport
              orders={currentDateBiasValue?.orderList}
              total={currentDateBiasValue?.totals?.total}
              reportSettings={currentDateBiasValue?.reportSetting}
              debitTransfersSentRequests={currentDateBiasValue?.debitTransfers?.debitTransfersFromUser || EMPTY_ARRAY}
              menuEvents={menuEvents}
            />
        ) : (
          <TransactionTable
            isMobile={isMinLargeTablet}
            orderList={currentDateBiasValue?.orderList}
            menuEvents={menuEvents}
            allPayments={currentDateBiasValue?.totals?.payments}
            currentLanguageKey={currentLanguageKey}
            dateBias={numberDateBias}
            totals={currentDateBiasValue?.totals}
            filter={filter}
            setFilter={setFilter}
            showEmployerEmployeePart={!isKlaReport && currentDateBiasValue?.reportSetting?.showEmployerEmployeePart}
            shouldShowPaymentRemarks={currentDateBiasValue?.reportSetting?.showPaymentRemarks}
            showTransferDebit={currentDateBiasValue?.reportSetting?.showTransferDebit}
            debitTransfersSentRequests={currentDateBiasValue?.debitTransfers?.debitTransfersFromUser || EMPTY_ARRAY}
            t={t}
            isAutoRefundRemarksPatchIncluded={currentDateBiasValue?.reportSetting?.showAutoRefundRemarks}
            showGrossNet={currentDateBiasValue?.reportSetting?.isIntelReport}
            totalGross={currentDateBiasValue?.moneycards[0]?.usageBruto?.monthly}
            totalNett={currentDateBiasValue?.moneycards[0]?.usageNet?.monthly}
            isKlaReport={isKlaReport}
            isTenbisCreditByMoneycardID={isTenbisCreditByMoneycardID}
            hideTitle={!isCompanyUser}
            hideFilters={!isCompanyUser}
            withRoundedCorners
            tableHeaderCustomColor={theme.colors.contentDisabled}
            customTableFooterColor={theme.colors.gray920}
            renderDesktopComponent={isMinLargeTablet}
            onMobileTransactionClick={onMobileTransactionClick}
          />
        )}
        </ContentWrapper>
      )}
    </Root>
  );
};

export default memo(MyOrders);
